/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import * as FlexmonsterReact from "react-flexmonster";
import { db } from "../../../firebaseConfig";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import Papa from "papaparse";
import { customizeToolbar } from "./CustomToolbar";
// import ColorPaletteSelector from "../Flexmonster/ColorPaletteSelector";
// import GoogleSheetsAuth from "../auth/GoogleSheetsAuth";
import GooglePicker from "../GooglePicker/GooglePicker";
import RecentLibrary from "../recents/RecentLibrary";
import TitleModal from "../modals/titleModal";
import getFlexmonsterConfig from "./config";

// Flexmonster CSS
import "../flexmonster.css";

const TableToolbar = forwardRef(({ componentId, viewId, config, isPresentationMode, onEditing, user }, ref) => {
  const [flexmonsterConfig, setFlexmonsterConfig] = useState();
  const [isGooglePickerOpen, setIsGooglePickerOpen] = useState(false);
  const [oauthToken, setOauthToken] = useState(null);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false); // New state variable for the modal
  const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
  const [saveToLibraryFunction, setSaveToLibraryFunction] = useState(null);
  const [originalFileName, setOriginalFileName] = useState(null); // Track original filename for library saving

  useEffect(() => {
    // Fetch Flexmonster configuration (license key)
    const fetchConfig = async () => {
      try {
        const config = await getFlexmonsterConfig();
        setFlexmonsterConfig(config);
      } catch (error) {
        console.error("Error fetching Flexmonster configuration:", error);
      }
    };
    if (!flexmonsterConfig) fetchConfig();
  }, [flexmonsterConfig]);

  const pivotRef = useRef();
  const userID = user.uid;

  const tableData = config;

  const saveTableData = useCallback(
    async (newTableData) => {
      const docRef = doc(db, "views", viewId);
      try {
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          let contentArray = docSnap.data().contents || [];
          const index = contentArray.findIndex((comp) => comp.id === componentId);

          const newContent = {
            id: componentId,
            content: JSON.stringify(newTableData),
            cards: contentArray[index]?.cards || 3,
            type: "data-visualization",
          };

          if (index !== -1) {
            contentArray[index] = newContent;
          } else {
            contentArray.push(newContent);
          }

          await updateDoc(docRef, { contents: contentArray });
        } else {
          console.error("Document does not exist!");
        }
      } catch (error) {
        console.error("Error updating Flexmonster data in Firestore:", error);
      }
    },
    [componentId, viewId]
  );

  // -- Gets and returns the chart options from the report -- \\
  const getChartOptions = useCallback(() => {
    if (pivotRef.current && pivotRef.current.flexmonster) {
      const report = pivotRef.current.flexmonster.getReport();
      if (report && report.options && report.options.viewType === "charts") {
        return {
          viewType: report.options.viewType,
          chart: report.options.chart,
        };
      }
    }
    return {
      viewType: "grid",
      chart: null,
    };
  }, [pivotRef]);

  const handleCSVUpload = useCallback(
    async (csvContent) => {
      const processParsedData = async (parsedData) => {
        const jsonContent = parsedData.data.map((row) => {
          return Object.fromEntries(Object.entries(row).map(([key, value]) => [key, value || ""]));
        });

        const dataSource = {
          type: "json",
          data: jsonContent,
        };
        const chartOptions = getChartOptions();

        // Extract title from filename if csvContent is a File object or Blob with name
        let title = "Uploaded CSV Data";
        let filename = null;
        if (csvContent instanceof File && csvContent.name) {
          // Remove file extension for cleaner title
          title = csvContent.name.replace(/\.[^/.]+$/, "");
          filename = csvContent.name.replace(/\.[^/.]+$/, "");
          // Store the original filename for later use in save to library
          setOriginalFileName(filename);
        } else if (csvContent instanceof Blob && csvContent.name) {
          // Handle named blobs from Google Picker
          title = csvContent.name.replace(/\.[^/.]+$/, "");
          filename = csvContent.name.replace(/\.[^/.]+$/, "");
          setOriginalFileName(filename);
        } else if (typeof csvContent === "string") {
          title = "CSV Data";
        }

        await saveTableData(dataSource, chartOptions, title);
        if (pivotRef.current && pivotRef.current.flexmonster) {
          pivotRef.current.flexmonster.setReport({
            dataSource: dataSource,
            options: chartOptions,
          });
        }
      };

      let parsedData;
      if (csvContent instanceof Blob) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target.result;
          parsedData = Papa.parse(text, { header: true });
          processParsedData(parsedData);
        };
        reader.readAsText(csvContent);
      } else {
        parsedData = Papa.parse(csvContent, { header: true });
        processParsedData(parsedData);
      }
    },
    [getChartOptions, pivotRef, saveTableData]
  );

  const handleJSONUpload = useCallback(
    async (jsonFile) => {
      if (jsonFile instanceof File) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const jsonContent = JSON.parse(e.target.result);
            const dataSource = {
              type: "json",
              data: Array.isArray(jsonContent) ? jsonContent : [jsonContent],
            };
            const chartOptions = getChartOptions();

            // Extract title from filename
            const title = jsonFile.name.replace(/\.[^/.]+$/, "");
            const filename = jsonFile.name.replace(/\.[^/.]+$/, "");
            setOriginalFileName(filename);

            await saveTableData(dataSource, chartOptions, title);
            if (pivotRef.current && pivotRef.current.flexmonster) {
              pivotRef.current.flexmonster.setReport({
                dataSource: dataSource,
                options: chartOptions,
              });
            }
          } catch (error) {
            console.error("Error parsing JSON file:", error);
          }
        };
        reader.readAsText(jsonFile);
      }
    },
    [getChartOptions, pivotRef, saveTableData]
  );

  const handleRemoteDataLoad = async (url, type) => {
    try {
      const response = await fetch(url);
      let data;
      if (type === "json") {
        data = await response.json();
      } else if (type === "csv") {
        const text = await response.text();
        const parsedData = Papa.parse(text, { header: true });
        data = parsedData.data;
      } else {
        // Handle other blob types if needed
        throw new Error(`Unsupported data type: ${type}`);
      }
      const jsonContent = data.map((row) => {
        return Object.fromEntries(Object.entries(row).map(([key, value]) => [key, value || ""]));
      });

      const dataSource = {
        type: "json",
        data: jsonContent,
      };
      const chartOptions = getChartOptions();
      // Extract filename from URL for title
      const urlParts = url.split("/").pop().split("\\").pop();
      const cleanTitle = urlParts.replace(/\.[^/.]+$/, "");
      const title = cleanTitle || `Remote Data - ${url}`;
      // Store filename for save to library
      if (cleanTitle && cleanTitle !== urlParts) {
        setOriginalFileName(cleanTitle);
      }
      await saveTableData(dataSource, chartOptions, title);
      if (pivotRef.current && pivotRef.current.flexmonster) {
        pivotRef.current.flexmonster.setReport({
          dataSource: dataSource,
          options: chartOptions,
        });
      }
    } catch (error) {
      console.error("Error fetching remote data:", error);
    }
  };
  const handleReportChange = async () => {
    if (pivotRef.current && pivotRef.current.flexmonster) {
      const report = pivotRef.current.flexmonster.getReport();
      const chartOptions = getChartOptions();
      if (report && report.dataSource && report.dataSource.filename) {
        const fileType = report.dataSource.type === "csv" ? "csv" : "json";
        await handleRemoteDataLoad(report.dataSource.filename, fileType);
      } else {
        await saveTableData(report, chartOptions);
      }

      if (report.slice && report.slice.rows && report.slice.columns) {
        onEditing();
      }
    }
  };

  const enableEvents = () => {
    const flexmonster = pivotRef.current?.flexmonster;

    if (flexmonster && pivotRef?.current) {
      flexmonster.on("reportchange", handleReportChange);
      flexmonster.on("datachanged", handleReportChange);
      flexmonster.on("celledit", handleReportChange);

      return () => {
        flexmonster.off("reportchange", handleReportChange);
        flexmonster.off("datachanged", handleReportChange);
        flexmonster.off("celledit", handleReportChange);
      };
    }
  };

  const handleLibraryModalToggle = (isOpen) => {
    setIsLibraryOpen(isOpen); // Update the state in ViewPage
  };

  const handleLibraryDataLoad = async (library) => {
    if (library?.contents?.content) {
      const newTableData = JSON.parse(library.contents.content); // Get the first content item

      // Extract title from various sources
      let title = library.title || "Loaded from Library";

      // If the library has a data source with filename, extract filename for title
      if (newTableData?.dataSource?.filename) {
        const filename = newTableData.dataSource.filename;
        // Extract filename from URL or path
        const filenameParts = filename.split("/").pop().split("\\").pop();
        // Remove file extension for cleaner title
        const cleanTitle = filenameParts.replace(/\.[^/.]+$/, "");
        if (cleanTitle && cleanTitle !== filenameParts) {
          title = cleanTitle;
        } else if (filenameParts) {
          title = filenameParts;
        }
      }

      // Set the report in Flexmonster
      if (pivotRef.current && pivotRef.current.flexmonster) {
        try {
          pivotRef.current.flexmonster.setReport(newTableData);
        } catch (error) {
          console.error("Error setting report in Flexmonster:", error);
        }

        // Save the report data to Firestore with the extracted title
        const chartOptions = getChartOptions();
        await saveTableData(newTableData, chartOptions, title);
      }
      // Close the library modal
      handleLibraryModalToggle(false);
    } else {
      console.error("No contents found in the library to load.");
    }
  };

  const handleGooglePickerClicked = () => {
    setIsGooglePickerOpen(true);
  };

  const handleTitleModalToggle = (isOpen) => {
    setIsTitleModalOpen(isOpen);
  };

  const handleSaveWithTitle = (title) => {
    if (saveToLibraryFunction) {
      saveToLibraryFunction(title);
    }
    setIsTitleModalOpen(false);
  };

  const onSaveWithTitleCallback = (saveFunction) => {
    setSaveToLibraryFunction(() => saveFunction);
  };

  useImperativeHandle(ref, () => ({
    exportTo: (type, params, callback) => {
      if (pivotRef.current && pivotRef.current.flexmonster) {
        pivotRef.current.flexmonster.exportTo(type, params, callback);
      }
    },
    getReport: () => {
      if (pivotRef.current && pivotRef.current.flexmonster) {
        return pivotRef.current.flexmonster.getReport();
      }
      return null;
    }
  }));

  if (!flexmonsterConfig?.licenseKey) {
    return null;
  }

  return pivotRef && config ? (
    <div className="component-content">
      {isLibraryOpen && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            // Only close if the overlay itself is clicked, not its children
            if (e.target === e.currentTarget) {
              handleLibraryModalToggle(false);
            }
          }}
        >
          <div className="modal-content modal-content-wide">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "1rem" }}>
              <h2>Connections</h2>
              <button onClick={() => handleLibraryModalToggle(false)}>Close</button>
            </div>
            <div className="modal-list">
              <RecentLibrary showActions={false} onLibraryDataLoad={handleLibraryDataLoad} />
            </div>
          </div>
        </div>
      )}

      <TitleModal
        isOpen={isTitleModalOpen}
        onClose={() => setIsTitleModalOpen(false)}
        onConfirm={handleSaveWithTitle}
        initialTitle={originalFileName || "New Library Item"}
      />

      <FlexmonsterReact.Pivot
        ref={pivotRef}
        toolbar={!isPresentationMode}
        height={640}
        report={tableData}
        beforetoolbarcreated={(toolbar) =>
          customizeToolbar(
            pivotRef.current.flexmonster,
            FlexmonsterReact.flexmonster,
            toolbar,
            handleCSVUpload,
            viewId,
            componentId,
            userID,
            handleLibraryModalToggle,
            handleGooglePickerClicked,
            handleTitleModalToggle,
            onSaveWithTitleCallback,
            handleJSONUpload
          )
        }
        ready={enableEvents}
        // -- FM API KEY -- \\
        licenseKey={flexmonsterConfig.licenseKey}
      />

      <GooglePicker
        isRequested={isGooglePickerOpen}
        onCSVBlobsCreated={handleCSVUpload}
        onClose={() => setIsGooglePickerOpen(false)}
        oauthToken={oauthToken}
        setOauthToken={setOauthToken}
      />
    </div>
  ) : null;
});

export default TableToolbar;
